import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, ArrowClockwise, ArrowCounterClockwise, MagnifyingGlass, Plus,
  UploadSimple,
} from "@phosphor-icons/react";
import LeadImportDialog from "@/components/LeadImportDialog";

function formatTalk(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m ${r}s`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
}

function formatLeadValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v instanceof Date || (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v))) {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const LEAD_FIELD_LABELS = {
  phone: "Phone",
  phone_code: "Phone code",
  phone_digits: "Phone digits",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  status: "Status",
  eligible: "Eligible",
  call_count: "Dial count",
  last_disposition: "Last disposition",
  last_called_at: "Last called",
  list_id: "List ID",
  campaign_id: "Campaign ID",
  id: "Lead ID",
  created_at: "Created",
  updated_at: "Updated",
  title: "Title",
  middle_name: "Middle name",
  address1: "Address 1",
  address2: "Address 2",
  address3: "Address 3",
  city: "City",
  state: "State",
  postal_code: "Postal code",
  province: "Province",
  vendor_id: "Vendor ID",
  gender: "Gender",
  alt_phone: "Alt phone",
  comments: "Comments",
  notes: "Notes",
  show: "Show",
};

const LEAD_DETAIL_SKIP = new Set([
  "tenant_id", "deleted_at", "reserved_by", "reserved_until", "extra",
]);

function leadDetailRows(lead) {
  if (!lead) return [];
  const rows = [];
  const coreOrder = [
    "phone", "phone_code", "first_name", "last_name", "email", "status",
    "eligible", "call_count", "last_disposition", "last_called_at",
    "list_id", "campaign_id", "id", "created_at", "updated_at",
  ];
  const seen = new Set();
  for (const key of coreOrder) {
    if (!(key in lead) || LEAD_DETAIL_SKIP.has(key)) continue;
    seen.add(key);
    rows.push({
      key,
      label: LEAD_FIELD_LABELS[key] || key.replace(/_/g, " "),
      value: formatLeadValue(lead[key]),
    });
  }
  const extra = lead.extra && typeof lead.extra === "object" ? lead.extra : {};
  const extraKeys = Object.keys(extra).sort();
  for (const key of extraKeys) {
    seen.add(`extra.${key}`);
    rows.push({
      key: `extra.${key}`,
      label: LEAD_FIELD_LABELS[key] || key.replace(/_/g, " "),
      value: formatLeadValue(extra[key]),
      section: "extra",
    });
  }
  for (const key of Object.keys(lead).sort()) {
    if (seen.has(key) || LEAD_DETAIL_SKIP.has(key)) continue;
    rows.push({
      key,
      label: LEAD_FIELD_LABELS[key] || key.replace(/_/g, " "),
      value: formatLeadValue(lead[key]),
    });
  }
  return rows;
}

const emptyLead = () => ({
  phone: "", first_name: "", last_name: "", email: "",
});

export default function CustomerListDetail() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadPage, setLeadPage] = useState(1);
  const [leadQ, setLeadQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadsLoading, setLeadsLoading] = useState(false);

  const [importOpen, setImportOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLead());
  const [savingLead, setSavingLead] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetIncludeDnc, setResetIncludeDnc] = useState(false);
  const [resetCallCount, setResetCallCount] = useState(true);
  const [resetting, setResetting] = useState(false);

  const [calledOpen, setCalledOpen] = useState(false);
  const [calledIncludeDnc, setCalledIncludeDnc] = useState(false);
  const [resettingCalled, setResettingCalled] = useState(false);

  const [activityLead, setActivityLead] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const [detailsLead, setDetailsLead] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-leads/lists/${listId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      if (e.response?.status === 404) navigate("/lists", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [listId, navigate]);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = { list_id: listId, page: leadPage, page_size: 50 };
      if (leadQ.trim()) params.q = leadQ.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/customer-leads/leads", { params });
      setLeads(data.items || []);
      setLeadTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLeadsLoading(false);
    }
  }, [listId, leadPage, leadQ, statusFilter]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const stats = detail?.stats;
  const statusRows = stats?.statuses || [];
  const statusSub = stats?.status_subtotals || {};
  const called = stats?.called_counts || {};
  const dispMetrics = stats?.disposition_metrics || {};
  const buckets = called.buckets || ["0", "1", "2", "3"];

  const statusOptions = useMemo(
    () => Object.entries(detail?.status_labels || {}).map(([code, name]) => ({ code, name })),
    [detail?.status_labels],
  );

  const saveLead = async () => {
    if (!leadForm.phone.trim()) { toast.error("Phone is required"); return; }
    setSavingLead(true);
    try {
      await api.post("/customer-leads/leads", {
        phone: leadForm.phone,
        first_name: leadForm.first_name || null,
        last_name: leadForm.last_name || null,
        email: leadForm.email || null,
        list_id: listId,
        status: "NEW",
      });
      toast.success("Lead added");
      setAddOpen(false);
      setLeadForm(emptyLead());
      loadDetail();
      loadLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSavingLead(false);
    }
  };

  const doReset = async () => {
    if (!window.confirm(
      "Reset dial status for leads in this list? They will return to NEW"
      + (resetCallCount ? " and call counts will be cleared." : "."),
    )) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/customer-leads/lists/${listId}/reset-dial-status`, {
        include_dnc: resetIncludeDnc,
        reset_call_count: resetCallCount,
        target_status: "NEW",
      });
      toast.success(`Reset ${data.modified} lead(s)`);
      if (data.pd_error) toast.warning("Dialer hopper was not reachable — counts there kept");
      setResetOpen(false);
      if (data.stats) {
        setDetail((d) => (d ? { ...d, stats: data.stats, lead_count: data.stats.lead_count } : d));
      } else {
        loadDetail();
      }
      loadLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setResetting(false);
    }
  };

  const doResetCalled = async () => {
    setResettingCalled(true);
    try {
      const { data } = await api.post(`/customer-leads/lists/${listId}/reset-called-status`, {
        include_dnc: calledIncludeDnc,
      });
      toast.success(`${data.modified} lead(s) marked not called`);
      if (data.pd_error) toast.warning("Dialer hopper was not reachable — counts there kept");
      setCalledOpen(false);
      if (data.stats) {
        setDetail((d) => (d ? { ...d, stats: data.stats, lead_count: data.stats.lead_count } : d));
      } else {
        loadDetail();
      }
      loadLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setResettingCalled(false);
    }
  };

  const openActivity = async (lead) => {
    setActivityLead(lead);
    setActivity(null);
    setActivityLoading(true);
    try {
      const { data } = await api.get(`/customer-leads/leads/${lead.id}/activity`);
      setActivity(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setActivityLead(null);
    } finally {
      setActivityLoading(false);
    }
  };

  const openDetails = async (lead) => {
    setDetailsLead(lead);
    setDetailsData(null);
    setDetailsLoading(true);
    try {
      const { data } = await api.get(`/customer-leads/leads/${lead.id}`);
      setDetailsData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setDetailsLead(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const detailRows = useMemo(() => leadDetailRows(detailsData), [detailsData]);

  if (loading && !detail) {
    return (
      <div className="space-y-6" data-testid="list-detail-page">
        <p className="text-sm text-muted-foreground">Loading list…</p>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="space-y-6" data-testid="list-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 -ml-2 text-muted-foreground"
            onClick={() => navigate("/lists")}
            data-testid="list-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Lists
          </Button>
          <PageHeader
            title={detail.name}
            subtitle={
              detail.list_kind === "inbound" || detail.system_list
                ? "System Inbound list — incoming caller information (not assignable to campaigns)"
                : (detail.description || "List detail — dial status, called counts, and leads")
            }
          />
          {(detail.list_kind === "inbound" || detail.system_list) && (
            <span
              className="inline-flex rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
              data-testid="list-detail-system-badge"
            >
              System · not campaign-assignable
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-lg" onClick={() => { setLeadForm(emptyLead()); setAddOpen(true); }} data-testid="list-detail-add-lead">
            <Plus className="w-4 h-4 mr-1.5" /> Add lead
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={() => setImportOpen(true)} data-testid="list-detail-import">
            <UploadSimple className="w-4 h-4 mr-1.5" /> Import
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={() => setCalledOpen(true)} data-testid="list-detail-reset-called">
            <ArrowCounterClockwise className="w-4 h-4 mr-1.5" /> Reset called status
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={() => setResetOpen(true)} data-testid="list-detail-reset">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reset dial status
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border bg-card p-4">
          <p className="text-xs text-muted-foreground">Leads</p>
          <p className="text-2xl font-semibold tabular-nums" data-testid="list-detail-lead-count">{detail.lead_count ?? stats?.lead_count ?? 0}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-xs text-muted-foreground">Dialable</p>
          <p className="text-2xl font-semibold tabular-nums">{statusSub.dialable ?? 0}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-xs text-muted-foreground">Penetration</p>
          <p className="text-2xl font-semibold tabular-nums">{statusSub.penetration_pct ?? 0}%</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-xs text-muted-foreground">Status</p>
          <div className="mt-1"><StatusPill status={detail.status} /></div>
        </div>
      </div>

      <div
        className="rounded-xl border bg-card p-4 space-y-3"
        data-testid="list-detail-disposition-metrics"
        title={dispMetrics.notes?.drop_vs_pdrop || ""}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            Disposition metrics
          </h2>
          <p className="text-[11px] text-muted-foreground">
            Unique leads · attempts {dispMetrics.total_attempts ?? 0}
          </p>
        </div>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 text-sm">
          {[
            ["Called", dispMetrics.called_leads],
            ["Not called", dispMetrics.not_called_leads],
            ["Live answers", dispMetrics.live_answers],
            ["Machine (AA)", dispMetrics.machine_answers],
            ["DROP", dispMetrics.drop_abandoned],
            ["PDROP", dispMetrics.pdrop],
            ["Busy", dispMetrics.busy],
            ["No answer", dispMetrics.no_answer],
            ["Invalid (DC)", dispMetrics.invalid_disconnected],
            ["Callbacks", dispMetrics.pending_callbacks],
            ["DNC", dispMetrics.dnc_suppressed],
            ["Sales", dispMetrics.sales],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-muted/40 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className="font-semibold tabular-nums">{value ?? 0}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          DROP = abandoned after answer while waiting for an agent. PDROP = dropped before agent queue. Not the same as call-level Drop Ratio.
        </p>
      </div>

      <div className="rounded-xl border bg-card p-4 space-y-2" data-testid="list-detail-campaigns">
        <p className="text-sm font-semibold">Assigned campaigns</p>
        {(detail.list_kind === "inbound" || detail.system_list) ? (
          <p className="text-sm text-muted-foreground">
            The Inbound list cannot be assigned to campaigns.
          </p>
        ) : (detail.campaigns || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Not assigned to any campaign yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {detail.campaigns.map((c) => (
              <Link
                key={c.id}
                to="/campaigns"
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-muted"
              >
                <span className="font-medium">{c.name}</span>
                <StatusPill status={c.status} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Statuses within this list */}
      <div className="space-y-2" data-testid="list-detail-statuses">
        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
          Statuses within this list
        </h2>
        {statusRows.length === 0 ? (
          <EmptyState title="No leads" description="Add or import leads to see status breakdown." />
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Status name</TableHead>
                  <TableHead className="text-right">Called</TableHead>
                  <TableHead className="text-right">Not called</TableHead>
                  <TableHead className="text-right">Dialable</TableHead>
                  <TableHead className="text-right">Penetration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statusRows.map((r) => (
                  <TableRow
                    key={r.status}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => { setStatusFilter(r.status); setLeadPage(1); }}
                  >
                    <TableCell className="font-mono text-xs font-semibold">{r.status}</TableCell>
                    <TableCell className="text-sm">{r.status_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.called}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.not_called}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.dialable}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.penetration_pct}%</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell colSpan={2}>Subtotals</TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.called ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.not_called ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.dialable ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.penetration_pct ?? 0}%</TableCell>
                </TableRow>
                <TableRow className="font-semibold">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right tabular-nums" colSpan={2}>
                    {(statusSub.called || 0) + (statusSub.not_called || 0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.dialable ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{statusSub.penetration_pct ?? 0}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Called counts */}
      <div className="space-y-2" data-testid="list-detail-called-counts">
        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
          Called counts within this list
        </h2>
        {(called.rows || []).length === 0 ? null : (
          <div className="rounded-xl border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Status name</TableHead>
                  {buckets.map((b) => (
                    <TableHead key={b} className="text-right bg-muted/30">{b}</TableHead>
                  ))}
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(called.rows || []).map((r) => (
                  <TableRow key={r.status}>
                    <TableCell className="font-mono text-xs font-semibold">{r.status}</TableCell>
                    <TableCell className="text-sm">{r.status_name}</TableCell>
                    {buckets.map((b) => (
                      <TableCell key={b} className="text-right tabular-nums text-primary">
                        {r.counts?.[b] || 0}
                      </TableCell>
                    ))}
                    <TableCell className="text-right tabular-nums font-medium">{r.subtotal}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell colSpan={2}>Total</TableCell>
                  {buckets.map((b) => (
                    <TableCell key={b} className="text-right tabular-nums">
                      {called.totals?.[b] || 0}
                    </TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums">{called.grand_total ?? 0}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Leads in list */}
      <div className="space-y-3" data-testid="list-detail-leads">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            Leads in this list
          </h2>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                className="pl-8 w-48"
                placeholder="Search leads…"
                value={leadQ}
                onChange={(e) => { setLeadPage(1); setLeadQ(e.target.value); }}
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setLeadPage(1); setStatusFilter(v); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statusOptions.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {leadsLoading ? (
          <p className="text-sm text-muted-foreground">Loading leads…</p>
        ) : leads.length === 0 ? (
          <EmptyState title="No leads" description="Add a lead or import a CSV into this list." />
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Dial count</TableHead>
                  <TableHead>Last called</TableHead>
                  <TableHead className="w-48" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.phone}</TableCell>
                    <TableCell className="text-sm">
                      {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-right tabular-nums">{r.call_count ?? 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.last_called_at
                        ? new Date(r.last_called_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-lg"
                          onClick={() => openActivity(r)}
                          data-testid={`list-lead-activity-${r.id}`}
                        >
                          Activity
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-lg"
                          onClick={() => openDetails(r)}
                          data-testid={`list-lead-details-${r.id}`}
                        >
                          Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {leadTotal > 50 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{leadTotal} leads</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={leadPage <= 1} onClick={() => setLeadPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={leadPage * 50 >= leadTotal} onClick={() => setLeadPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <LeadImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        list={detail}
        onImported={() => { loadDetail(); loadLeads(); }}
      />

      {/* Add lead */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add lead to {detail.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} data-testid="list-detail-lead-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name</Label>
                <Input value={leadForm.first_name} onChange={(e) => setLeadForm({ ...leadForm, first_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Last name</Label>
                <Input value={leadForm.last_name} onChange={(e) => setLeadForm({ ...leadForm, last_name: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" onClick={saveLead} disabled={savingLead}>{savingLead ? "Saving…" : "Add lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset dial status */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset dial status</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Set leads in this list back to <strong>NEW</strong> so they can be dialed again.
            DNC is excluded unless you opt in below.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={resetCallCount} onChange={(e) => setResetCallCount(e.target.checked)} />
            Also reset dial / call counts to 0
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={resetIncludeDnc} onChange={(e) => setResetIncludeDnc(e.target.checked)} />
            Include DNC leads
          </label>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" onClick={doReset} disabled={resetting} data-testid="list-detail-reset-confirm">
              {resetting ? "Resetting…" : "Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset called status */}
      <Dialog open={calledOpen} onOpenChange={setCalledOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset called status</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Mark leads in this list as <strong>not called</strong>. Dial statuses are
            kept as they are — call counts go back to 0 and the dialer drops its
            attempt counters, so leads it had given up on can be dialed again.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={calledIncludeDnc} onChange={(e) => setCalledIncludeDnc(e.target.checked)} />
            Include DNC leads
          </label>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setCalledOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" onClick={doResetCalled} disabled={resettingCalled} data-testid="list-detail-reset-called-confirm">
              {resettingCalled ? "Resetting…" : "Reset called status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead details */}
      <Dialog open={!!detailsLead} onOpenChange={(v) => !v && setDetailsLead(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-details-dialog">
          <DialogHeader>
            <DialogTitle>
              Lead details{detailsLead ? ` — ${detailsLead.phone}` : ""}
            </DialogTitle>
          </DialogHeader>
          {detailsLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!detailsLoading && detailsData && (
            <div className="space-y-3 text-sm" data-testid="lead-details-panel">
              <dl className="rounded-lg border divide-y">
                {detailRows.map((row) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[9rem_1fr] gap-3 px-3 py-2"
                    data-testid={`lead-detail-row-${row.key}`}
                  >
                    <dt className="text-xs text-muted-foreground capitalize pt-0.5">{row.label}</dt>
                    <dd className={`break-words ${row.key === "phone" || row.key === "id" ? "font-mono text-xs" : ""}`}>
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lead activity */}
      <Dialog open={!!activityLead} onOpenChange={(v) => !v && setActivityLead(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Lead activity{activityLead ? ` — ${activityLead.phone}` : ""}
            </DialogTitle>
          </DialogHeader>
          {activityLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {activity && (
            <div className="space-y-4 text-sm" data-testid="lead-activity-panel">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-[11px] text-muted-foreground">Dial count</p>
                  <p className="text-lg font-semibold tabular-nums">{activity.dial_count}</p>
                </div>
                <div className="rounded-lg border p-3 col-span-2">
                  <p className="text-[11px] text-muted-foreground">Total talk time</p>
                  <p className="text-lg font-semibold tabular-nums">{formatTalk(activity.total_talk_sec)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Agents who talked</p>
                {(activity.agents || []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">No agent talk recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {activity.agents.map((a) => (
                      <li key={a.user_id} className="flex justify-between gap-2 rounded-md border px-2.5 py-1.5">
                        <span>
                          <span className="font-medium">{a.name}</span>
                          {a.email && <span className="text-muted-foreground text-xs ml-1">{a.email}</span>}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {a.calls} call{a.calls === 1 ? "" : "s"} · {formatTalk(a.talk_sec)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Recent calls</p>
                {(activity.recent_calls || []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">No CDRs for this lead.</p>
                ) : (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Disposition</TableHead>
                          <TableHead className="text-right">Talk</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activity.recent_calls.slice(0, 15).map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="text-xs">
                              {c.start_time ? new Date(c.start_time).toLocaleString() : "—"}
                            </TableCell>
                            <TableCell className="text-xs">{c.disposition || "—"}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums">
                              {formatTalk(c.duration_sec)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
